import {
    createPopper,
    Instance,
    Modifier,
} from '@popperjs/core';

import Component from '../component/component';
import Helpers from '../helpers/helpers';

import './dropdown.scss';

export default class Dropdown extends Component {
    static initSelector: string = '.dropdown';

    trigger: HTMLElement;
    content: HTMLElement;
    inputs: NodeListOf<HTMLInputElement>;
    triggerLabel: NodeListOf<HTMLElement>;
    filterContainer: NodeListOf<HTMLElement>;
    filterAmount: NodeListOf<HTMLElement>;
    filterClear: NodeListOf<HTMLElement>;
    selectedContainer: HTMLElement;
    dropdownGroups: HTMLElement;
    defaultText: string;
    inContent: boolean;
    popperInstance: Instance;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    modifiers: Partial<Modifier<any, any>>[];
    boundingElement: HTMLElement;
    outsideClickHandler: () => void;
    screenWidth: number;

    constructor(target: HTMLElement) {
        super(target);

        this.trigger = this.element.querySelector('.dropdown__trigger');
        this.content = this.element.querySelector('.dropdown__content');
        this.boundingElement = document.querySelector('.' + this.element.getAttribute('bounding-element'));
        this.modifiers = [
            {
                name: 'flip',
                options: {
                    padding: [0],
                },
            },
            {
                name: 'offset',
                options: {
                    offset: [0, 20],
                },
            },
            {
                name: 'preventOverflow',
                options: {
                    boundary: this.boundingElement,
                    padding: [0],
                },
            },
        ];
        this.inputs = this.element.querySelectorAll('.dropdown__group .choice-group__item input');
        this.triggerLabel = this.element.querySelectorAll('.dropdown__trigger-text');
        this.filterContainer = this.element.querySelectorAll('.dropdown__filter-container');
        this.filterAmount = this.element.querySelectorAll('.dropdown__filter-amount');
        this.filterClear = this.element.querySelectorAll('.dropdown__clear-filters');
        this.selectedContainer = this.element.querySelector('.dropdown__selected-container');
        this.dropdownGroups = this.element.querySelector('.dropdown__groups');
        this.outsideClickHandler = this.onOutsideClick.bind(this);
        this.screenWidth = window.innerWidth;

        if (this.triggerLabel.length) {
            this.defaultText = this.triggerLabel[0].textContent;
        }

        this.init();
    }

    init(): void {
        this.element.addEventListener('dropdown.show', this.show.bind(this));
        this.element.addEventListener('dropdown.hide', this.hide.bind(this));
        this.trigger.addEventListener('click', this.toggle.bind(this));

        this.popperInstance = createPopper(this.trigger, this.content, {
            modifiers: this.modifiers,
            placement: 'bottom-start',
        });

        this.handleDefaultSelection();
        this.calculateSelectedContainerSize();

        this.inputs.forEach((input: HTMLInputElement) => input.addEventListener('change', this.handleInputChange.bind(this)));
        this.inputs.forEach((input: HTMLInputElement) => input.addEventListener('click', this.closeOnRadioClick.bind(this)));
        this.filterClear.forEach((element: HTMLElement) => {
            element.addEventListener('click', this.clearFiltersHandler.bind(this));
        });
        window.removeEventListener('resize', this.handleResize.bind(this));
        window.addEventListener('resize', this.handleResize.bind(this));

        window.addEventListener('enhanceReset', this.removeOutsideClickHandler.bind(this));
    }

    get isOpen(): boolean {
        return this.content.classList.contains('is-visible');
    }

    toggle(event: Event): void {
        if (this.isOpen) {
            this.hide();
        } else {
            this.show(event);
        }
    }

    show(event: Event | CustomEvent): void {
        const disableAnimation: boolean = !!(event instanceof CustomEvent ? event?.detail?.disableAnimation : false);

        if (disableAnimation) {
            this.element.classList.add('is-animation-disabled');
        }

        if (event.type === 'click' && Helpers.isMobileDevice) {
            this.trigger.focus();
        }

        this.content.classList.add('is-visible');
        this.element.classList.add('is-open');

        window.addEventListener('click', this.outsideClickHandler);

        this.content.addEventListener('mouseenter', this.onMouseEnterContent.bind(this));
        this.content.addEventListener('mouseleave', this.onMouseLeaveContent.bind(this));

        this.popperInstance.setOptions({
            modifiers: [...this.modifiers, {
                enabled: true,
                name: 'eventListeners',
            }],
        });

        this.popperInstance.update();

        if (disableAnimation) {
            this.element.classList.remove('is-animation-disabled');
        }
    }

    hide(event?: CustomEvent): void {
        const disableAnimation: boolean = !!(event?.detail?.disableAnimation);

        if (disableAnimation) {
            this.element.classList.add('is-animation-disabled');
        }

        this.content.classList.remove('is-visible');
        this.element.classList.remove('is-open');

        this.inContent = false;

        this.content.removeEventListener('mouseenter', this.onMouseEnterContent.bind(this));
        this.content.removeEventListener('mouseleave', this.onMouseLeaveContent.bind(this));

        this.popperInstance.setOptions({
            modifiers: [...this.modifiers, {
                enabled: false,
                name: 'eventListeners',
            }],
        });


        window.removeEventListener('click', this.outsideClickHandler);

        if (disableAnimation) {
            this.element.classList.remove('is-animation-disabled');
        }
    }

    onMouseEnterContent(): void {
        this.inContent = true;
    }

    onMouseLeaveContent(): void {
        this.inContent = false;
    }

    onOutsideClick(event: MouseEvent): void {
        if (Helpers.outsideClick(event, this.element) && this.isOpen) {
            event.preventDefault();

            this.inContent = false;
            this.hide();
        }
    }

    removeOutsideClickHandler(): void {
        window.removeEventListener('click', this.outsideClickHandler);
    }

    handleDefaultSelection(): void {
        const radioSelection: NodeListOf<HTMLInputElement> = this.element.querySelectorAll('.radio__input:checked');

        if (radioSelection.length === 2) {
            let text: string = this.defaultText;

            radioSelection.forEach((element: HTMLElement) => Helpers.parents(element, '.radio').forEach((radio: HTMLElement) => text = radio.querySelector('.radio__text').textContent));
            this.triggerLabel.forEach((element: HTMLElement) => {
                element.textContent = text;
            });
        }
    }

    closeOnRadioClick(event: JQuery.TriggeredEvent): void {
        const target: HTMLInputElement = event.currentTarget as HTMLInputElement;

        if (target.classList.contains('radio__input')) {
            document.querySelectorAll('.modal-container.is-visible').forEach((item: HTMLElement) => item.dispatchEvent(new CustomEvent('modal.close')));
            Helpers.enableScroll();
            this.hide();
            this.inContent = false;
            this.calculateSelectedContainerSize();
        }
    }

    handleInputChange(event: Event): void {
        const target: HTMLInputElement = event.currentTarget as HTMLInputElement;

        this.changeDuplicateInput(target, event);
        const selectedInputs: NodeListOf<HTMLInputElement> = this.dropdownGroups.querySelectorAll('input:checked');

        if (selectedInputs.length === 0) {
            this.triggerLabel.forEach((element: HTMLElement) => {
                element.textContent = this.defaultText;
            });
            this.hideFilters();
        } else if (selectedInputs.length === 1) {
            const parents: HTMLElement[] = Helpers.parents(selectedInputs[0], '.choice-group__field');
            let text: string = this.defaultText;

            parents.forEach((parent: HTMLElement) => {
                parent.querySelectorAll('.check__text, .radio__text').forEach((item: HTMLElement) => text = item.textContent);
            });

            this.triggerLabel.forEach((element: HTMLElement) => {
                element.textContent = text;
            });

            this.hideFilters();
        } else {
            this.showFilters(target, selectedInputs);
        }
    }

    showFilters(target: HTMLElement, selectedInputs: NodeListOf<HTMLInputElement>): void {
        this.triggerLabel.forEach((element: HTMLElement) => {
            element.classList.add('h-hidden');
        });
        this.filterContainer.forEach((element: HTMLElement) => {
            element.classList.remove('h-hidden');
        });
        this.filterAmount.forEach((element: HTMLElement) => {
            element.innerText = `${selectedInputs.length}`;
        });
    }

    hideFilters(): void {
        this.triggerLabel.forEach((element: HTMLElement) => {
            element.classList.remove('h-hidden');
        });
        this.filterContainer.forEach((element: HTMLElement) => {
            element.classList.add('h-hidden');
        });
    }

    changeDuplicateInput(target: HTMLInputElement, event?: Event): void {
        const id: string = target.getAttribute('id');
        let duplicateId: string;

        if (id.includes('dropdown-modal')) {
            duplicateId = id.replace('dropdown-modal-', '');
        } else {
            duplicateId = 'dropdown-modal-' + id;
        }

        if (duplicateId) {
            const element: HTMLElement = document.getElementById(duplicateId);

            if (element) {
                if (element instanceof HTMLInputElement) {
                    element.checked = target.checked;
                }

                if (!event || event.isTrusted) {
                    element.dispatchEvent(new Event('dropdown.duplicateChange'));
                }
            }
        }
    }

    clearFiltersHandler(): void {
        const selectedInputs: NodeListOf<HTMLInputElement> = this.element.querySelectorAll('input:checked');

        selectedInputs.forEach((element: HTMLInputElement) => {
            element.checked = false;
            element.dispatchEvent(new Event('change'));
            this.changeDuplicateInput(element);
        });

        this.triggerLabel.forEach((element: HTMLElement) => {
            element.textContent = this.defaultText;
        });

        this.hideFilters();
    }

    handleResize(): void {
        const newScreenWidth: number = window.innerWidth;

        if (newScreenWidth !== this.screenWidth) {
            document.querySelectorAll('.modal-container.is-visible').forEach((item: HTMLElement) => item.dispatchEvent(new CustomEvent('modal.close')));
            Helpers.enableScroll();
            this.hide();
            this.inContent = false;
            this.calculateSelectedContainerSize();
        }
    }

    calculateSelectedContainerSize(): void {
        const labelSize: number = this.element.querySelector('.dropdown__label').clientWidth;
        const selectedWidth: number = this.element.offsetWidth - 180 - labelSize;

        this.selectedContainer.style.maxWidth = `${selectedWidth}px`;
    }
}
